import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ABILITY, type Run } from "~/api/types";
import { isLive } from "~/features/runs/runDerive";
import { Button } from "~/foundation/components/Button";
import { cn } from "~/foundation/utils/cn";

/**
 * A control that says why it cannot be pressed.
 *
 * The `title` rides a wrapping span, not the button: `Button`'s disabled state sets
 * `pointer-events-none`, so a title on the button itself would never surface for the one
 * reader who needs it. It is set on both so a test can read either.
 */
function Verb({
  label,
  glyph,
  directional,
  disabled,
  reason,
  danger,
  active,
  onClick,
}: {
  label: string;
  glyph: string;
  /** Glyphs that point somewhere must flip with the writing direction. */
  directional?: boolean;
  disabled?: boolean;
  reason?: string;
  danger?: boolean;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <span className="inline-flex" title={disabled ? reason : undefined}>
      <Button
        size="sm"
        variant={danger ? "danger" : active ? "primary" : "neutral"}
        disabled={disabled === true}
        onClick={onClick}
        {...(disabled && reason ? { title: reason } : {})}
      >
        <span aria-hidden className={cn(directional && "rtl:rotate-180")}>
          {glyph}
        </span>
        {label}
      </Button>
    </span>
  );
}

export interface RunControlsProps {
  run: Run;
  /** How many breakpoints are set right now — the chip row's own count, not the run DTO's. */
  breakpointCount: number;
  /** False when the run carries no graph snapshot; the breakpoint row has nothing to list. */
  hasGraph: boolean;
  breakpointsOpen: boolean;
  steeringOpen: boolean;
  /** `run.aiFlowSlug` — the server never projects it today (F-3), so this is always null. */
  flowSlug: string | null;
  canvasMode: "canvas" | "graphs";
  busy: boolean;
  onPause: () => void;
  onResume: () => void;
  onStep: () => void;
  onJump: () => void;
  onStop: () => void;
  onRetry: () => void;
  onToggleBreakpoints: () => void;
  onToggleSteering: () => void;
  onToggleCanvasMode: () => void;
  onOpenLedger: () => void;
  children?: ReactNode;
}

/**
 * The header verb strip. Every button here is enabled by the run's own `can` block and by
 * nothing else — never by the status, never by the viewer's role. The server computes which
 * verbs have somewhere real to land (an executor polling a control gate, a paused cursor to
 * move); a client that re-derives that offers buttons which report success while the run
 * keeps going.
 *
 * F-4 (fixed server-side, BRIEF-CGNI slice 3): a *server-hosted* flow run used to report
 * `can.step/jump/cancel = false` although the endpoints accept it; `RunProjection.ToDto` now
 * projects the host-aware forms (runs.list in one batch lookup). The cure for a wrong `false`
 * was a server fix, not a guess here — keep honouring `can`.
 */
export function RunControls(props: RunControlsProps) {
  const { t } = useTranslation();
  const { run } = props;
  const can = run.abilities;
  const live = isLive(run.status);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {live ? (
        <>
          {can[ABILITY.runResume] === true || run.status === "paused" ? (
            <Verb
              label={t("Resume")}
              glyph="▶"
              directional
              disabled={can[ABILITY.runResume] !== true || props.busy}
              reason={t(
                "The server is not offering resume on this run — either it is not paused, or it has no executor to reach.",
              )}
              onClick={props.onResume}
            />
          ) : (
            <Verb
              label={t("Pause")}
              glyph="⏸"
              disabled={can[ABILITY.runPause] !== true || props.busy}
              reason={t(
                "The server is not offering pause on this run — either it is not running, or a control request is already in flight.",
              )}
              onClick={props.onPause}
            />
          )}
          <Verb
            label={t("Step")}
            glyph="⤼"
            directional
            disabled={can[ABILITY.runStep] !== true || props.busy}
            reason={t(
              "This run has no registered IDE executor polling a control gate, so stepping it would do nothing.",
            )}
            onClick={props.onStep}
          />
          <Verb
            label={t("Jump…")}
            glyph="⏭"
            directional
            disabled={can[ABILITY.runJump] !== true || props.busy}
            reason={t("Pause the run before moving its cursor.")}
            onClick={props.onJump}
          />
          <Verb
            label={t("Stop")}
            glyph="⏹"
            danger
            disabled={can[ABILITY.runCancel] !== true || props.busy}
            reason={t("The server is not offering a stop on this run — it has already finished.")}
            onClick={props.onStop}
          />
        </>
      ) : null}

      {props.hasGraph ? (
        <Verb
          label={
            props.breakpointCount > 0
              ? t("Breakpoints · {{count}}", { count: props.breakpointCount })
              : t("Breakpoints")
          }
          glyph="◉"
          active={props.breakpointsOpen}
          onClick={props.onToggleBreakpoints}
        />
      ) : null}

      <Verb
        label={t("Steering")}
        glyph="🧭"
        active={props.steeringOpen}
        disabled={!props.flowSlug}
        reason={t("This run is not linked to an ai-flow slug, so it has no directive channel.")}
        onClick={props.onToggleSteering}
      />

      <Verb
        label={props.canvasMode === "canvas" ? t("Graphs") : t("Canvas")}
        glyph={props.canvasMode === "canvas" ? "📈" : "◧"}
        onClick={props.onToggleCanvasMode}
      />

      <Verb
        label={t("Run again")}
        glyph="↻"
        disabled={can[ABILITY.runRetry] !== true || props.busy}
        reason={t(
          "Only advisor and backtest runs can be re-run from here; a flow or digest run is started again from its flow.",
        )}
        onClick={props.onRetry}
      />

      <Verb label={t("Ledger")} glyph="🧾" onClick={props.onOpenLedger} />

      {props.children}
    </div>
  );
}
